import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Button, Row, Col } from 'react-bootstrap';
import { injectIntl } from 'react-intl';
import { forEach as _forEach } from 'lodash';
import moment from 'moment';

import { getNameInCurrentLanguage } from 'helpers/intl';
import { formStatusDispatchHelper } from 'actions/FormStatusActions';
import { DOC_TYPE_DRIVERS_LICENSE } from 'helpers/docs';

import EntityStateUpdater from 'component/EntityStateUpdater';

import Loader from 'components/layout/Loader';
import Data from 'components/Data';
import Entity from 'components/Entity';

import FormInput from 'components/forms/FormInput';
import FormFile from 'components/forms/FormFile';
import FormRichEditor from 'components/forms/FormRichEditor';
import DatePicker from 'components/forms/DatePicker';
import FormErrors from 'components/forms/FormErrors';

import { entityMetaMapping } from 'js/constants';
import {
  docRequiredNumber,
  docRequiredIssuingCity,
  docRequiredIssuingAuthority,
  docRequiredIssueDate,
  docRequiredExpirationDate,
  injectValidator,
} from 'formValidation/entityValidators';

class DocUpload extends EntityStateUpdater {
  constructor(props) {
    super(props);

    this.state = {
      files: [],
      entityLoaded: false,
      entityChanged: false,
      currentType: null,
      isDriversLicense: false,
    };

    this.entity = new Entity();
    // if we get document data from the outside, we can pass it on to our data entity
    const data = props.doc || {};
    if (!data.name && data.type) {
      data.name = getNameInCurrentLanguage(data.type.name, props.appIntl);
    }
    if (data.type && data.type.id) {
      if (data.type.id === DOC_TYPE_DRIVERS_LICENSE) {
        this.state.isDriversLicense = true;
      }
    }

    this.entity.setData(data);
    if (props.meta[entityMetaMapping.doc] !== undefined && !props.meta[entityMetaMapping.doc].pending) {
      this.entity.setMeta(props.meta, entityMetaMapping.doc);

      this.state = {
        ...this.state,
        entityLoaded: true,
        entity: this.entity,
      };
    }

    this.defaultDocExpirationDate = moment()
      .add(1, 'years')
      .toISOString();
    this.defaultDocIssueDate = moment()
      .subtract(2, 'years')
      .toISOString();

    this.docNumberRef = React.createRef();
    this.docIssuingAuthorityRef = React.createRef();
    this.docIssueDateRef = React.createRef();
    this.docIssuingCityRef = React.createRef();
    this.docExpirationDateRef = React.createRef();
  }

  componentDidUpdate = prevProps => {
    const { intl, meta, formStatus, textractDriversLicense, onSuccess } = this.props;
    const { entity, entityLoaded, currentType } = this.state;

    // check if we could extract data from drivers license
    if (
      !textractDriversLicense.pending &&
      !textractDriversLicense.hasError &&
      prevProps.textractDriversLicense.pending &&
      textractDriversLicense.response.success
    ) {
      const { response } = textractDriversLicense;
      this.updateEntityMultiple(
        ['issuingAuthority', 'issueDate', 'number'],
        [response.issuing_authority, response.issue_date, response.number],
        [],
        () => {
          this.docIssuingAuthorityRef.current.setValue(response.issuing_authority);
          // this.docIssueDateRef.current.setValue(response.issue_date); // the date value is passed by props only
          this.docNumberRef.current.setValue(response.number);
        }
      );
    }

    if (
      meta[entityMetaMapping.doc] !== undefined &&
      !meta[entityMetaMapping.doc].pending &&
      !this.entity.getMetaLoaded()
    ) {
      this.entity.setMeta(meta, entityMetaMapping.doc);
      if (this.entity.getDataLoaded()) {
        this.setState({ entityLoaded: true, entity: this.entity });
      }
    }

    // check if we need to add extra validators
    if (entityLoaded && entity.getType() && entity.getType().getId() && currentType !== entity.getType().getId()) {
      const newEntity = { ...entity };
      newEntity.resetValidators();

      if (newEntity.getType().getRequireNumber()) {
        newEntity.addValidator(injectValidator(docRequiredNumber, intl));
      }

      if (newEntity.getType().getRequireIssuingCity()) {
        newEntity.addValidator(injectValidator(docRequiredIssuingCity, intl));
      }

      if (newEntity.getType().getRequireIssuingAuthority()) {
        newEntity.addValidator(injectValidator(docRequiredIssuingAuthority, intl));
      }

      if (newEntity.getType().getRequireIssueDate()) {
        newEntity.addValidator(injectValidator(docRequiredIssueDate, intl));
      }

      if (newEntity.getType().getRequireExpirationDate()) {
        newEntity.addValidator(injectValidator(docRequiredExpirationDate, intl));
      }

      this.changeEntity(newEntity, null, false, { currentType: entity.getType().getId() });
    }

    if (prevProps.formStatus.pending && !formStatus.pending && !formStatus.hasError && onSuccess) {
      onSuccess(formStatus.response);
    }
  };

  componentDidMount = () => {
    if (this.state.entityLoaded && this.state.entity.getType() && this.state.entity.getType().getId()) {
      // check if we need to add extra validators
      const entity = { ...this.state.entity };
      let reloadEntity = false;

      if (entity.getType().getRequireNumber()) {
        reloadEntity = true;
        entity.addValidator(injectValidator(docRequiredNumber, this.props.intl));
      }

      if (entity.getType().getRequireIssuingCity()) {
        reloadEntity = true;
        entity.addValidator(injectValidator(docRequiredIssuingCity, this.props.intl));
      }

      if (entity.getType().getRequireIssuingAuthority()) {
        reloadEntity = true;
        entity.addValidator(injectValidator(docRequiredIssuingAuthority, this.props.intl));
      }

      if (entity.getType().getRequireIssueDate()) {
        reloadEntity = true;
        entity.addValidator(injectValidator(docRequiredIssueDate, this.props.intl));
      }

      if (entity.getType().getRequireExpirationDate()) {
        reloadEntity = true;
        entity.addValidator(injectValidator(docRequiredExpirationDate, this.props.intl));
      }

      if (reloadEntity) {
        this.changeEntity(entity, null, false);
      }
    }
  };

  onSubmit = e => {
    e.preventDefault();
    e.stopPropagation();

    this.setState(
      prevState => ({ entity: prevState.entity.validate() }),
      () => {
        if (this.state.entity.isValid) {
          this.dispatchFormSubmit();
        }
      }
    );
  };

  dispatchFormSubmit = () => {
    const fd = new FormData();

    // append files
    _forEach(this.state.files, (file, key) => {
      fd.append(key, file);
    });

    // append data
    // include the form context so we know where the document was uploaded from
    fd.append(
      'json',
      JSON.stringify({
        ...this.state.entity.processedData,
        contexts: this.props.formContext ? [this.props.formContext] : [],
      })
    );

    this.props.onSubmit(fd);
  };

  getFormFiles = () => {
    const {
      intl: { messages },
      appIntl,
    } = this.props;
    const { entity } = this.state;

    const result = [];

    if (entity.getType() && entity.getType().getId() && entity.getType().getPageInfos().length) {
      _forEach(entity.getType().getPageInfos(), (pageInfo, i) => {
        result.push(
          <FormFile
            key={i}
            label={getNameInCurrentLanguage(pageInfo.getName(), appIntl)}
            name={'file' + i}
            onChange={this.setFile}
          />
        );
      });
    } else {
      result.push(<FormFile key="1" label={messages.file} name="file0" onChange={this.setFile} />);
    }

    return result;
  };

  setFile = (fieldName, files) => {
    const { doTextractDriversLicense } = this.props;
    const { files: stateFiles, isDriversLicense } = this.state;

    // if it's the drivers license and it's the first file
    // extract the data from the image file
    const callback =
      isDriversLicense && fieldName === 'file0'
        ? () => {
            doTextractDriversLicense(files[0]);
          }
        : null;

    this.setState({ files: { ...stateFiles, [fieldName]: files[0] } }, callback);
  };

  updateType = (name, value) => {
    const isDriversLicense = value && value.id === DOC_TYPE_DRIVERS_LICENSE;

    this.setState({ isDriversLicense }, () => this.updateEntity(name, value));
  };

  showDescription = e => {
    e.preventDefault();
    this.setState({ showDescription: true });
  };

  render() {
    const {
      intl: { messages },
      appIntl,
      formStatus,
      user,
    } = this.props;
    const { entity, entityLoaded, showDescription } = this.state;

    return (
      <Row>
        <Data dataName="doc" /> {/* fetch meta data */}
        <Col xs={12}>
          <FormErrors formStatus={formStatus} />

          {entityLoaded ? (
            <form method="post" onSubmit={this.onSubmit}>
              <div className="panel panel-transparent">
                <div className="form-group-attached">
                  <FormInput
                    name="name"
                    label={messages.name}
                    value={entity.getName()}
                    meta={entity.getNameMetaInformation()}
                    validation={entity.getNameValidationResult()}
                    onChange={this.updateEntity}
                    disabled={formStatus.pending}
                  />
                  {showDescription ? (
                    <FormRichEditor
                      name="description"
                      label={messages.description}
                      value={entity.getDescription()}
                      meta={entity.getDescriptionMetaInformation()}
                      validation={entity.getDescriptionValidationResult()}
                      onChange={this.updateEntity}
                      disabled={formStatus.pending}
                    />
                  ) : (
                    <div className="form-group form-group-default p-t-10 p-b-10">
                      <a href="#editDescription" className="text-master" onClick={this.showDescription}>
                        {messages.edit_description} <i className="fal fa-chevron-right m-l-10" />
                      </a>
                    </div>
                  )}

                  {!!entity.getType() && !!entity.getType().getId() && (
                    <>
                      {this.getFormFiles()}

                      {entity.getType().getRequireNumber() && (
                        <FormInput
                          name="number"
                          ref={this.docNumberRef}
                          label={messages.doc_number}
                          value={entity.getNumber()}
                          meta={entity.getNumberMetaInformation()}
                          validation={entity.getNumberValidationResult()}
                          onChange={this.updateEntity}
                          disabled={formStatus.pending}
                        />
                      )}

                      {entity.getType().getRequireIssuingCity() && (
                        <FormInput
                          name="issuingCity"
                          ref={this.docIssuingCityRef}
                          label={messages.doc_issuing_city}
                          value={entity.getIssuingCity()}
                          meta={entity.getIssuingCityMetaInformation()}
                          validation={entity.getIssuingCityValidationResult()}
                          onChange={this.updateEntity}
                          disabled={formStatus.pending}
                        />
                      )}

                      {entity.getType().getRequireIssuingAuthority() && (
                        <FormInput
                          name="issuingAuthority"
                          ref={this.docIssuingAuthorityRef}
                          label={messages.doc_issuing_authority}
                          value={entity.getIssuingAuthority()}
                          meta={entity.getIssuingAuthorityMetaInformation()}
                          validation={entity.getIssuingAuthorityValidationResult()}
                          onChange={this.updateEntity}
                          disabled={formStatus.pending}
                        />
                      )}

                      {entity.getType().getRequireIssueDate() && (
                        <DatePicker
                          name="issueDate"
                          ref={this.docIssueDateRef}
                          label={messages.doc_issue_date}
                          value={entity.getIssueDate() ? entity.getIssueDate() : this.defaultDocIssueDate}
                          meta={entity.getIssueDateMetaInformation()}
                          validation={entity.getIssueDateValidationResult()}
                          locale={appIntl.locale}
                          displayTz={user.profile.timezone}
                          onChange={this.updateEntity}
                          disabled={formStatus.pending}
                        />
                      )}

                      {entity.getType().getRequireExpirationDate() && (
                        <DatePicker
                          name="expirationDate"
                          ref={this.docExpirationDateRef}
                          label={messages.doc_expiration_date}
                          value={
                            entity.getExpirationDate() ? entity.getExpirationDate() : this.defaultDocExpirationDate
                          }
                          meta={entity.getExpirationDateMetaInformation()}
                          validation={entity.getExpirationDateValidationResult()}
                          locale={appIntl.locale}
                          displayTz={user.profile.timezone}
                          onChange={this.updateEntity}
                          disabled={formStatus.pending}
                        />
                      )}
                    </>
                  )}
                </div>

                <div className="text-right m-t-15">
                  <Button
                    bsSize="lg"
                    disabled={formStatus.pending}
                    type="submit"
                    bsStyle={this.state.entityChanged ? 'primary' : 'default'}
                  >
                    <i className={'fa-fw ' + (formStatus.pending ? ' fa fa-circle-notch fa-spin' : 'fal fa-save')} />{' '}
                    {messages.save}
                  </Button>
                </div>
              </div>
            </form>
          ) : (
            <Loader />
          )}
        </Col>
      </Row>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  let formStatus = null;
  switch (ownProps.uploadTarget) {
    case 'lead':
      formStatus = state.formStatus.leadDocumentNew;
      break;

    case 'client':
      formStatus = state.formStatus.documentNew;
      break;

    case 'order':
      formStatus = state.formStatus.orderDocumentNew;
      break;
  }

  return {
    appIntl: state.intl,
    user: state.login.user,
    meta: state.entityMeta,
    formContext: state.formStatus.context,
    formStatus,
    textractDriversLicense: state.formStatus.textractDriversLicense,
  };
};
const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    onSubmit: args => {
      switch (ownProps.uploadTarget) {
        case 'lead':
          formStatusDispatchHelper(
            'leadDocumentNew',
            '/api/leads/' + ownProps.leadId + '/docs.json',
            args,
            'post',
            dispatch
          );
          break;

        case 'client':
          formStatusDispatchHelper(
            'documentNew',
            '/api/clients/' + ownProps.targetId + '/docs.json',
            args,
            'post',
            dispatch
          );
          break;

        case 'order':
          formStatusDispatchHelper(
            'orderDocumentNew',
            '/api/orders/' + ownProps.orderId + '/docs.json',
            args,
            'post',
            dispatch
          );
          break;
      }
    },
    doTextractDriversLicense: file => {
      const fd = new FormData();
      fd.append('file', file);
      formStatusDispatchHelper('textractDriversLicense', '/api/textract/driverslicense.json', fd, 'post', dispatch);
    },
  };
};

export default withRouter(
  injectIntl(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(DocUpload)
  )
);
